/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react';
import {
  Formik,
  Field,
  FieldArray,
  ArrayHelpers,
} from 'formik';

import {
  Icon,
  Button,
  TimeTextField,
  TextField,
  SelectField,
} from 'web-components';

import {
  Flex,
  Text,
  SVG,
} from 'web-styled';

import { FormData } from '../utils';
import * as S from '../styled';

const getPossibleNumbers = (options: any[]) => options?.reduce(
  (all, item) => {
    if (!item.dieroll_range_end) return [...all, item.dieroll_range_start];

    const itemRange = Array.from({
      length: item.dieroll_range_end - item.dieroll_range_start + 1,
    }, (_, k) => k + item.dieroll_range_start);

    return [...all, ...itemRange];
  },
  [],
);

const getPropertyShortName = (p: any) => {
  if (!p) return '';

  const du = p.default_unit;
  return du.short_name === 'mmss' ? 'minutes' : du.short_name;
};

const getDefaultMin = (secs: number) => {
  const mind = secs % (60 * 60);
  return Math.floor(mind / 60);
};

const getDefaultSec = (secs: number) => {
  const mind = secs % (60 * 60);
  return Math.ceil(mind % 60);
};

/*
 * Example
 * 2
 * 2-4
 * */
const numberMask = (rawValue = '') => {
  const digitRegExp = /[1-6]/;
  if (rawValue.split('').length < 2) return [digitRegExp];

  return [digitRegExp, '–', digitRegExp];
};

type Props = {
  initialValues: FormData;
  propertiesMap: any;
  propertyOptions: any[];
  onSubmit: { (values: FormData, actions: any): void };
  onCancel: { (): void };
};

const BasicForm = ({
  initialValues,
  propertiesMap,
  propertyOptions,
  onSubmit,
  onCancel,
}: Props) => {
  const [destroyed, setDestroyed] = useState([]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values, actions) => {
        const options = [...destroyed, ...values.options];
        onSubmit(options, actions);
      }}
    >
      {(formikProps) => (
        <Flex
          column
          as="form"
          width="100%"
          spacing={15}
          onSubmit={formikProps.handleSubmit}
        >
          <FieldArray
            name="options"
          >
            {(arrayHelpers: ArrayHelpers) => (
              <Flex
                column
                spacing={15}
                width="100%"
              >
                <Flex width="100%" spacing="15">
                  <Field
                    name="range"
                    label="When die rolls"
                    color="primary"
                    width="120px"
                    mask={numberMask}
                    guide={false}
                    placeholder="Ex: 1, 2-3"
                    component={TextField}
                  />
                  <Field
                    name="metric"
                    label="Select metric"
                    color="primary"
                    width="50%"
                    placeholder="Select metric"
                    options={propertyOptions}
                    component={SelectField}
                  />
                  <Field
                    name="rawValue"
                    label="Value"
                    color="primary"
                    width="115px"
                    placeholder="0"
                    component={getPropertyShortName(propertiesMap[formikProps.values.metric]) === 'minutes'
                      ? TimeTextField
                      : TextField}
                  />
                  <Flex align="center" margin="36px 0 0 0">
                    <Button
                      appearance="link"
                      onClick={() => {
                        formikProps.resetForm({
                          values: {
                            range: '',
                            metric: '',
                            rawValue: '',
                            options: formikProps.values.options,
                          },
                        });
                      }}
                    >
                      <Text size="16px">
                        clear all
                      </Text>
                    </Button>
                    <Button
                      icon={<SVG.Plus size={14} strokeWidth={4} />}
                      iconSize={16}
                      iconColor="primary"
                      appearance="empty"
                      onClick={() => {
                        formikProps.setFieldTouched('range', true);
                        formikProps.setFieldTouched('metric', true);
                        formikProps.setFieldTouched('rawValue', true);

                        const { range, metric, rawValue } = formikProps.values;
                        let start: number = -1;
                        let end;

                        const errors: any = {};
                        if (!metric) {
                          errors.metric = 'Required';
                        }

                        if (!rawValue) {
                          errors.rawValue = 'Required';
                        }

                        if (!range) {
                          errors.range = 'Required';
                        } else {
                          const splitRange = range.split('–');
                          start = parseInt(splitRange[0], 10);
                          end = splitRange[1] ? parseInt(splitRange[1], 10) : '';
                          if (end && start >= end) {
                            errors.range = 'Invalid range';
                          }
                        }

                        // check if the number on the range already added
                        const alreadyAdded = (formikProps.values.options || []).filter(
                          option => option.id === metric,
                        );
                        if (!errors.range && alreadyAdded?.length) {
                          const numbers = getPossibleNumbers(alreadyAdded);
                          if (numbers.indexOf(start) !== -1 || numbers.indexOf(end) !== -1) {
                            errors.range = 'Already added';
                          }
                        }

                        if (Object.keys(errors).length > 0) {
                          formikProps.setErrors(errors);
                          return;
                        }

                        formikProps.setFieldValue('range', '');
                        formikProps.setFieldTouched('range', false);
                        formikProps.setFieldValue('rawValue', '');
                        formikProps.setFieldTouched('rawValue', false);

                        const newRange = {
                          value: rawValue,
                          metric,
                          dieroll_range_start: start,
                          dieroll_range_end: end,
                        };

                        const index = formikProps.values.options?.findLastIndex(
                          (o: any) => (o.id === metric) && (o.dieroll_range_start < start),
                        );

                        if (index === -1 && !alreadyAdded.length) {
                          arrayHelpers.push(newRange);
                        } else {
                          arrayHelpers.insert(index + 1, newRange);
                        }
                      }}
                    />
                  </Flex>
                </Flex>

                {formikProps.values.options?.map((item, index) => (
                  <Flex
                    key={String(index)}
                    width="100%"
                    column
                  >
                    <hr
                      style={{
                        backgroundColor: '#C4C4C4',
                        marginBottom: '15px',
                        height: (index === 0 || formikProps.values.options[index - 1]?.metric !== item.metric) ? '3px' : '1px',
                      }}
                    />

                    <Flex
                      spacing={15}
                      width="100%"
                      align="center"
                      justify="space-between"
                    >
                      <Flex spacing={10}>
                        <Text color="#808084" size="14px">When die roll is</Text>
                        <Flex spacing="5">
                          <S.ItemValue>
                            {item.dieroll_range_start}
                          </S.ItemValue>
                          <Text color="#808084" size="14px">–</Text>
                          <S.ItemValue>
                            {item.dieroll_range_end}
                          </S.ItemValue>
                        </Flex>
                        <Text color="#808084" size="14px">
                          {`${propertiesMap[item.metric]?.name} is`}
                        </Text>
                        {getPropertyShortName(propertiesMap[item.metric]) === 'minutes' ? (
                          <Flex spacing="5">
                            <S.ItemValue>{getDefaultMin(item.value) || '00'}</S.ItemValue>
                            <span>:</span>
                            <S.ItemValue>{getDefaultSec(item.value) || '00'}</S.ItemValue>
                          </Flex>
                        ) : (
                          <>
                            <S.ItemValue>{item.value}</S.ItemValue>
                            <Text color="#808084" size="14px">
                              {getPropertyShortName(propertiesMap[item.metric])}
                            </Text>
                          </>
                        )}
                      </Flex>
                      <S.Remove
                        type="button"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          arrayHelpers.remove(index);
                          if (item.id) {
                            setDestroyed(arr => [
                              ...arr,
                              { ...item, _destroy: true },
                            ]);
                          }
                        }}
                      >
                        <SVG.TrashAlt size={14} />
                      </S.Remove>
                    </Flex>
                  </Flex>
                ))}
                <hr
                  style={{
                    backgroundColor: '#C4C4C4',
                    marginBottom: '15px',
                    height: '3px',
                  }}
                />
              </Flex>
            )}
          </FieldArray>

          {typeof formikProps.errors.options === 'string' && (
            <Text color="danger">
              {formikProps.errors.options}
            </Text>
          )}

          <Flex
            width="100%"
            justify="space-between"
            margin="30px 0 0 0"
          >
            <Button
              ghost
              width="140px"
              spacing="large"
              appearance="danger"
              isDisabled={formikProps.isSubmitting}
              onClick={onCancel}
            >
              Cancel
            </Button>

            <Button
              ghost
              width="140px"
              spacing="large"
              appearance="primary"
              isLoading={formikProps.isSubmitting}
              isDisabled={formikProps.isSubmitting}
              onClick={() => {
                const { options } = formikProps.values;
                if (!options.length) {
                  formikProps.setFieldError('options', 'Must have numbers');
                  return;
                }

                // check if some metric is incomplete
                for (let i = 0; i < propertyOptions.length; i += 1) {
                  const numbers = getPossibleNumbers(options.filter(
                    option => option.metric === propertyOptions[i].value,
                  ));

                  if (numbers.length > 0 && numbers.length < 6) {
                    formikProps.setFieldError('options', `${propertyOptions[i].label}: Range incomplete`);
                    return;
                  }
                }

                formikProps.submitForm();
              }}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      )}
    </Formik>
  );
};

export default BasicForm;
